import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
} from '@seen/ui'
import { type JSX, type ReactNode, useId } from 'react'
import { FacesheetCollapseTriangle } from '#src/components/patient/detail-content/facesheet/FacesheetCollapseTriangle.tsx'

export interface FacesheetCardSectionProps {
  title: string
  count?: number
  // Tailwind background utility for the leading status dot (e.g.
  // `bg-emerald-500`). Omit for a section with no status.
  dotClassName?: string
  // Renders the label as a disclosure. Sections that exist to keep noise out
  // of the way (temporary medications) start closed.
  collapsible?: boolean
  defaultOpen?: boolean
  children: ReactNode
  className?: string
}

export interface FacesheetCardSectionsProps {
  children: ReactNode
  className?: string
}

export const FACESHEET_CARD_ROW_CLASS = 'text-xs leading-4'
export const FACESHEET_CARD_LIST_CLASS = 'flex flex-col gap-1.5'

// Sibling subsection stack. gap-4 is the space between one section's last
// row and the next heading (Figma 814:9762, opened up so the two blocks
// do not run together).
export function FacesheetCardSections({
  children,
  className,
}: FacesheetCardSectionsProps): JSX.Element {
  return (
    <div className={cn('mt-1 flex flex-col gap-4', className)}>{children}</div>
  )
}

// Labelled group inside a facesheet card: status dot, title with its own
// count, and a divider that carries the sectioning instead of the all-caps
// label the cards used before (Figma "New header section style").
//
// Title sits 2px above the hairline; content sits 10px below so the line
// does not crowd the first row. Rows use a shared 6px gap so Medications,
// Observations, Conditions, and Allergies match.
//
// A collapsible section is a Collapsible rather than a conditional render so
// it animates like the card's own triangle and inherits CollapsibleContent's
// overflow handling — the rows here are dnd-kit reorder rows, which translate
// rather than reparent while dragging and would be clipped by a naive
// overflow-hidden.
export function FacesheetCardSection({
  title,
  count,
  dotClassName,
  collapsible = false,
  defaultOpen = true,
  children,
  className,
}: FacesheetCardSectionProps): JSX.Element {
  const labelId = useId()

  const label = (
    <>
      {dotClassName && (
        <span
          aria-hidden
          className={cn('size-1 shrink-0 rounded-full', dotClassName)}
        />
      )}
      <span id={labelId}>
        {count === undefined ? title : `${title} (${count})`}
      </span>
    </>
  )

  const divider = (
    <div aria-hidden className="h-px bg-stone-300 dark:bg-stone-700" />
  )
  const titleRowClassName =
    'flex gap-1.5 items-center min-h-4 text-xs font-semibold leading-4 tracking-[-0.12px]'

  if (!collapsible) {
    return (
      <div className={cn('flex flex-col gap-2.5', className)}>
        <div className="flex flex-col gap-0.5">
          <div className={titleRowClassName}>{label}</div>
          {divider}
        </div>
        <div className={FACESHEET_CARD_LIST_CLASS}>{children}</div>
      </div>
    )
  }

  return (
    <Collapsible
      defaultOpen={defaultOpen}
      className={cn('group/facesheet-section flex flex-col', className)}
    >
      <div className="flex flex-col gap-0.5">
        <CollapsibleTrigger asChild>
          {/* `unstyled`, not `ghost`: ghost tints its backdrop on
              aria-expanded, which an open disclosure always is. */}
          <Button
            variant="unstyled"
            size="unstyled"
            aria-labelledby={labelId}
            className={cn(
              titleRowClassName,
              'w-fit rounded-sm focus-visible:ring-2 focus-visible:ring-offset-0'
            )}
          >
            {label}
            <FacesheetCollapseTriangle className="group-data-[state=closed]/facesheet-section:-rotate-90" />
          </Button>
        </CollapsibleTrigger>
        {divider}
      </div>
      <CollapsibleContent className={cn(FACESHEET_CARD_LIST_CLASS, 'pt-2.5')}>
        {children}
      </CollapsibleContent>
    </Collapsible>
  )
}
